import React, { useEffect, useState } from "react";

// import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import "../../assets/CSS/modelform.css";

import StepsCard from "../../components/Cards/StepsCard";
import SelectMaterial from "../../components/ModelForm/SelectMaterial";
import ManageData from "../../components/ModelForm/ManageData";
import AddCalibration from "../../components/ModelForm/AddCalibration";
import ManageModel from "../../components/ModelForm/ManageModel";
import ModelsAPI from "../../API/models.api";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import LogCapture from "../../utils/LogManagement/LogHelper";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import MultistepCompletionHandler from "../../components/Handlers/MultistepCompletionHandler";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";
import {
    BOOLEAN_DROPDOWN,
    SORTING_DIRECTION_OPTIONS,
} from "../../utils/constants";
import { getMappedParameterLocalizations } from "../../utils/generalMappers";

const ModelForm = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] =
        useState(false);
    const [, setStepButtonDisabled] = useState(true);

    const { t } = useTranslation();

    const STEPS = [
        { number: "1", name: t("createdModelStep1") },
        { number: "2", name: t("createdModelStep2") },
        { number: "3", name: t("createdModelStep3") },
        { number: "4", name: t("createdModelStep4") },
        { number: "5", name: t("createdModelStep5") },
        { number: "6", name: t("createdModelStep6") },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/models",
            element: () => <Trans i18nKey="models">Models</Trans>,
        },
        {
            element: () => <Trans i18nKey="createModel">Create Model</Trans>,
        },
    ];

    const finalStepTitles = {
        isSubmitting: t("modelCreationSubmittingTitle"),
        isSubmitted: t("modelCreatingSubmitSuccessTitle"),
        default: t("modelCreationSubmitFailTitle"),
    };

    const finalStepContent = {
        isSubmitting: t("modelCreationSubmittingMessage"),
        isSubmitted: t("modelCreationSubmitSuccessMessage"),
        default: (
            <span>
                <Trans i18nKey="modelCreationSubmitFailMessage">
                    Your model could not be uploaded. Please check your internet
                    connection and try again. if you still face any issue please
                    contact us at,
                </Trans>
                <b> Support@si-ware.com</b>
            </span>
        ),
    };

    //Step 1
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [selectedMaterialName, setSelectedMaterialName] = useState("");

    //Step 2 & Step 3
    const [data, setData] = useState(null);
    const [dataFileName, setDataFileName] = useState("");

    const [dataReference, setDataReference] = useState({
        headers: [],
        rows: [],
    });
    const [dataReferenceFileName, setDataReferenceFileName] = useState("");
    const [modifiedDataReference, setModifiedDataReference] = useState({
        headers: [],
        rows: [],
    });

    const [calibrationHasMissingRef, setCalibrationHasMissingRef] =
        useState(false);
    const [settings, setSettings] = useState({});
    const [maxAllowedKFold, setMaxAllowedKFold] = useState(0);

    //Step 4 & Step 5
    const [testingData, setTestingData] = useState(null);
    const [testingDataFileName, setTestingDataFileName] = useState("");

    const [testingDataReference, setTestingDataReference] = useState({
        headers: [],
        rows: [],
    });
    const [testingDataReferenceFileName, setTestingDataReferenceFileName] =
        useState("");
    const [testingModifiedDataReference, setTestingModifiedDataReference] =
        useState({
            headers: [],
            rows: [],
        });

    const [testingHasMissingRef, setTestingHasMissingRef] = useState(false);
    const [testingSettings, setTestingSettings] = useState({});

    //Step 6
    const [modelInfo, setModelInfo] = useState({});
    const [activeUnits, setActiveUnits] = useState([]);
    const [, setMaxFoldValue] = useState(5);
    const [userAccess, setUserAccess] = useState([]);
    const [organizationAccess, setOrganizationAccess] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [, setIsSubmited] = useState(false);
    const [parametersSettings, setParametersSettings] = useState([]);

    const [modelOptions, setModelOptions] = useState({
        supportedAccessories: [],
        supportedDevices: [],
        enforceOutlierDetection: false,
        isDynamicSorting: false,
        sortingDirection: SORTING_DIRECTION_OPTIONS.ASC,
        isSortingByValues: BOOLEAN_DROPDOWN.true,
        dynamicSortedParameterNames: [],
    });

    useEffect(() => {
        if (
            (currentStep === 1 && selectedMaterial !== null) ||
            (currentStep === 2 && data)
        ) {
            setStepButtonDisabled(false);
        }
    }, [selectedMaterial, data]);

    useEffect(() => {
        modifiedDataReference.headers &&
            setActiveUnits(Array(modifiedDataReference.headers.length).fill(1));
    }, [modifiedDataReference]);

    const nextStep = (e, stepNumber) => {
        if (stepNumber) {
            setCurrentStep(stepNumber);
        } else {
            setCurrentStep((prev) => prev + 1);
        }
        setStepButtonDisabled(true);
    };

    const prevStep = (e, stepNumber) => {
        if (stepNumber) {
            setCurrentStep(stepNumber);
        } else {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const mapParametersSettings = () => {
        let mappedParameterSettings = {};
        parametersSettings.forEach((parameter, parameterIndex) => {
            const newParamDetails = [...parameter.parameterDetails];
            const baseUnit = newParamDetails.splice(0, 1);
            const units = newParamDetails.map((conversionUnit, unitIndex) => {
                if (conversionUnit.unit) {
                    return {
                        unitConversionId: conversionUnit.unit.value,
                        decimalPlaces: conversionUnit.decimalPlaces,
                        displayUnit:
                            activeUnits[parameterIndex] - 1 === unitIndex,
                    };
                }
            });
            mappedParameterSettings[parameter.name] = {
                order: parameterIndex,
                unitId: baseUnit[0].unit.value,
                decimalPlaces: baseUnit[0].decimalPlaces,
                modelParameterTranslations: getMappedParameterLocalizations(
                    parameter?.localizations
                ),
                units,
            };
        });
        return mappedParameterSettings;
    };

    const getModelOptions = () => {
        const supportedAccessoryIds = modelOptions?.supportedAccessories.map(
            (a) => a.value
        );
        const supportedDeviceIds = modelOptions?.supportedDevices.map(
            (a) => a.value
        );
        const dynamicSortedParameterNames =
            modelOptions?.dynamicSortedParameterNames.map((a) => a.value);

        return {
            supportedAccessoryIds,
            supportedDeviceIds,
            enforceOutlierDetection: modelOptions?.enforceOutlierDetection,
            isDynamicSorting: modelOptions?.isDynamicSorting,
            sortingDirection: modelOptions?.sortingDirection?.value,
            isSortingByValues: modelOptions?.isSortingByValues?.value,
            dynamicSortedParameterNames,
        };
    };

    const submitModel = () => {
        return new Promise((resolve, reject) => {
            let createModel = {
                modelName: modelInfo.name,
                materialId: selectedMaterial,
                description: modelInfo.description,
                image: modelInfo.image,
                options: modelInfo.options,
                parameterNames: modifiedDataReference.headers,
                calibrationIds: data.ids,
                calibrationReferences: modifiedDataReference.rows,
                testingIds: testingData?.ids,
                testingReferences: testingModifiedDataReference?.rows,
                modelUserAccesses: userAccess,
                modelOrganizationAccesses: organizationAccess,
                parametersSettings: mapParametersSettings(),
                settings,
                ...getModelOptions(),
                isCustom: false,
            };

            ModelsAPI.createModel(createModel)
                .then(() => {
                    setIsSuccessfullySubmitted(true);
                    SuccessAlert(t("createdModelSuccessMessage"));
                    LogCapture.info(
                        `Model created with name: ${modelInfo?.name}`,
                        ENTITIES.models,
                        ACTIONS.models.createModel
                    );
                    resolve(true);
                })
                .catch((err) => {
                    console.log(err);
                    ErrorAlert(t("createdModelFailMessage"));
                    reject(false);
                    LogCapture.error(
                        `Couldn't create model with name: ${modelInfo?.name}, error: ${err}`
                    );
                });
        });
    };

    const renderForm = () => {
        switch (currentStep) {
            case 1:
                return (
                    <SelectMaterial
                        selectedRow={selectedMaterial}
                        setSelectedRow={setSelectedMaterial}
                        setSelectedRowName={setSelectedMaterialName}
                        nextStepButtonText={t("createdModelStep2")}
                        nextStep={nextStep}
                    />
                );

            case 2:
                return (
                    <AddCalibration
                        data={data}
                        setData={setData}
                        dataFileName={dataFileName}
                        setDataFileName={setDataFileName}
                        dataReference={dataReference}
                        setDataReference={setDataReference}
                        dataReferenceFileName={dataReferenceFileName}
                        setDataReferenceFileName={setDataReferenceFileName}
                        modifiedDataReference={modifiedDataReference}
                        setModifiedDataReference={setModifiedDataReference}
                        settings={settings}
                        setSettings={setSettings}
                        setMaxFoldValue={setMaxFoldValue}
                        setHasMissingRef={setCalibrationHasMissingRef}
                        hasMissingRef={calibrationHasMissingRef}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        selectedMaterial={selectedMaterial}
                        selectedMaterialName={selectedMaterialName}
                        nextStepButtonText={t("createdModelStep3")}
                        setMaxAllowedKFold={setMaxAllowedKFold}
                    />
                );

            case 3:
                return (
                    <ManageData
                        data={data}
                        dataFileName={dataFileName}
                        dataReference={dataReference}
                        dataReferenceFileName={dataReferenceFileName}
                        modifiedDataReference={modifiedDataReference}
                        setModifiedDataReference={setModifiedDataReference}
                        setData={setData}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        nextStepButtonText={t("createdModelStep4")}
                    />
                );

            case 4:
                return (
                    <AddCalibration
                        data={testingData}
                        setData={setTestingData}
                        dataFileName={testingDataFileName}
                        setDataFileName={setTestingDataFileName}
                        dataReference={testingDataReference}
                        setDataReference={setTestingDataReference}
                        dataReferenceFileName={testingDataReferenceFileName}
                        setDataReferenceFileName={
                            setTestingDataReferenceFileName
                        }
                        modifiedDataReference={testingModifiedDataReference}
                        setModifiedDataReference={
                            setTestingModifiedDataReference
                        }
                        settings={testingSettings}
                        setSettings={setTestingSettings}
                        setMaxFoldValue={setMaxFoldValue}
                        setHasMissingRef={setTestingHasMissingRef}
                        hasMissingRef={testingHasMissingRef}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        selectedMaterial={selectedMaterial}
                        selectedMaterialName={selectedMaterialName}
                        nextStepButtonText={t("createdModelStep5")}
                        isTesting={true}
                    />
                );

            case 5:
                return (
                    <ManageData
                        data={testingData}
                        dataFileName={testingDataFileName}
                        dataReference={testingDataReference}
                        dataReferenceFileName={testingDataReferenceFileName}
                        modifiedDataReference={testingModifiedDataReference}
                        setModifiedDataReference={
                            setTestingModifiedDataReference
                        }
                        setData={setTestingData}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        nextStepButtonText={t("createdModelStep6")}
                    />
                );

            case 6:
                return (
                    <ManageModel
                        model={modelInfo}
                        setModel={setModelInfo}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        submitModel={submitModel}
                        isTesting={!testingData}
                        setUserAccess={setUserAccess}
                        setOrganizationAccess={setOrganizationAccess}
                        maxAllowedKFold={maxAllowedKFold}
                        isSubmitting={isSubmitting}
                        setIsSubmitting={setIsSubmitting}
                        setIsSubmited={setIsSubmited}
                        parameterNames={modifiedDataReference.headers}
                        parametersSettings={parametersSettings}
                        setParametersSettings={setParametersSettings}
                        scannerSettings={settings}
                        setScannerSettings={setSettings}
                        modelOptions={modelOptions}
                        setModelOptions={setModelOptions}
                        activeUnit={activeUnits}
                        setActiveUnit={setActiveUnits}
                    />
                );

            case 7:
                return (
                    <MultistepCompletionHandler
                        prevStep={prevStep}
                        isSubmitted={isSuccessfullySubmitted}
                        isSubmitting={isSubmitting}
                        titles={finalStepTitles}
                        content={finalStepContent}
                        successRedirect={"/models"}
                    />
                );
            default:
                return <div></div>;
        }
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <header className="main-model-form-header main-content-header flex items-center">
                <i className="fas fa-cog" />
                <h2 className="h2">
                    <Trans i18nKey="addNewModel"> New Model</Trans>
                </h2>
            </header>
            <StepsCard
                steps={STEPS}
                activeNumber={currentStep}
                className="mb-4"
            />

            {renderForm()}
        </>
    );
};

export default ModelForm;
